import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  NativeSelect,
  SimpleGrid,
  Text,
  Textarea,
  ButtonGroup,
  VStack,
  useDisclosure,
  Badge,
  Alert,
  Spinner,
  Dialog,
  Portal,
  Field,
} from '@chakra-ui/react';
import { Switch } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { InputGroup, InputLeftElement } from '@/components/ui/input-group';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaFileAlt,
  FaSearch,
  FaShareAlt,
  FaTrash,
  FaUpload,
  FaLock,
  FaCopy,
  FaWhatsapp,
  FaEye,
  FaEyeSlash,
  FaExternalLinkAlt,
  FaList,
  FaTh,
} from 'react-icons/fa';
import AppLayout from './AppLayout.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { bandasAPI } from '../services/api.js';
import { useProductTour } from '../hooks/useProductTour';
import { bandaDocumentosSteps } from '../tours/bandaDocumentosTour';

const MotionBox = motion.create(Box);

const CATEGORIAS = [
  { value: 'estatutos', label: 'Estatutos' },
  { value: 'normativa_interna', label: 'Normativa interna' },
  { value: 'actas', label: 'Actas' },
  { value: 'permisos_parentales', label: 'Permisos parentales' },
  { value: 'cruceta_planificacion', label: 'Cruceta / planificación anual' },
  { value: 'semana_santa', label: 'Normas de Semana Santa' },
  { value: 'otros', label: 'Otros' },
];

const labelCategoria = (c) => CATEGORIAS.find((x) => x.value === c)?.label || c;

const VISTA_STORAGE_KEY = 'cadencya.banda.documentos.vista';

const ORDEN_OPTIONS = [
  { value: 'predeterminado', label: 'Orden predeterminado' },
  { value: 'reciente', label: 'Más recientes primero' },
  { value: 'antiguo', label: 'Más antiguos primero' },
  { value: 'titulo_az', label: 'Título (A → Z)' },
  { value: 'titulo_za', label: 'Título (Z → A)' },
  { value: 'categoria', label: 'Por categoría' },
  { value: 'tamano_desc', label: 'Tamaño (mayor primero)' },
  { value: 'tamano_asc', label: 'Tamaño (menor primero)' },
];

export default function BandaDocumentosGestion() {
  
  const { user, profile } = useAuth();
  useProductTour('banda-documentos', bandaDocumentosSteps, { autoStart: false });
  const cardBg = useColorModeValue('white', 'gray.800');
  const subtle = useColorModeValue('gray.600', 'gray.400');
  const heroBg = useColorModeValue(
    'linear-gradient(135deg, #faf5ff 0%, #ffffff 50%, #ecfeff 100%)',
    'linear-gradient(135deg, rgba(88,28,135,0.25) 0%, rgba(30,32,44,0.95) 55%, rgba(6,78,78,0.15) 100%)',
  );
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const heroBorder = useColorModeValue('purple.100', 'whiteAlpha.200');
  const docRowBorder = useColorModeValue('gray.100', 'whiteAlpha.200');
  const docRowHoverBorder = useColorModeValue('purple.200', 'purple.400');
  const docRowHoverBg = useColorModeValue('purple.50', 'whiteAlpha.50');

  const isTitularBanda = useMemo(
    () =>
      Boolean(
        user?.userType === 'BANDA' &&
          (!profile?.userId || String(user?.id) === String(profile.userId)),
      ),
    [user?.userType, user?.id, profile?.userId],
  );

  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [categoria, setCategoria] = useState('estatutos');
  const [descripcion, setDescripcion] = useState('');
  const [file, setFile] = useState(null);
  const [publicadoTablonAlSubir, setPublicadoTablonAlSubir] = useState(false);
  const [patchingTablonId, setPatchingTablonId] = useState(null);

  const [busqueda, setBusqueda] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  /** '' = todos, 'tablon' = publicados en tablón, 'sin_tablon' = solo biblioteca */
  const [filtroTablon, setFiltroTablon] = useState('');
  const [orden, setOrden] = useState('predeterminado');
  const [openingId, setOpeningId] = useState(null);
  const [vista, setVista] = useState(() => {
    try {
      const v = localStorage.getItem(VISTA_STORAGE_KEY);
      return v === 'mosaico' ? 'mosaico' : 'lista';
    } catch {
      return 'lista';
    }
  });

  const setVistaPersist = useCallback((v) => {
    const next = v === 'mosaico' ? 'mosaico' : 'lista';
    setVista(next);
    try {
      localStorage.setItem(VISTA_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const [share, setShare] = useState({
    shareUrl: '',
    hasPassword: false,
    canRevealSharePassword: false,
  });
  const [shareLoading, setShareLoading] = useState(true);

  const { open: uploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure();

  const resetUploadForm = () => {
    setTitulo('');
    setDescripcion('');
    setCategoria('estatutos');
    setFile(null);
    setPublicadoTablonAlSubir(false);
  };

  const closeUploadModal = () => {
    onUploadClose();
    resetUploadForm();
  };

  const { open: modalPwdOpen, onOpen: onModalPwdOpen, onClose: onModalPwdClose } = useDisclosure();
  const [sharePwd, setSharePwd] = useState('');
  const [sharePwd2, setSharePwd2] = useState('');
  const [sharePwdSaving, setSharePwdSaving] = useState(false);

  const { open: revealOpen, onOpen: onRevealOpen, onClose: onRevealClose } = useDisclosure();
  const [verCuentaPwd, setVerCuentaPwd] = useState('');
  const [revealed, setRevealed] = useState('');
  const [showRevealed, setShowRevealed] = useState(false);
  const [revealLoading, setRevealLoading] = useState(false);

  const { open: cambiarOpen, onOpen: onCambiarOpen, onClose: onCambiarClose } = useDisclosure();
  const [cambiarActual, setCambiarActual] = useState('');
  const [cambiarNueva, setCambiarNueva] = useState('');
  const [cambiarNueva2, setCambiarNueva2] = useState('');
  const [cambiarSaving, setCambiarSaving] = useState(false);

  const {
    open: isDeleteDocOpen,
    onOpen: onDeleteDocOpen,
    onClose: onDeleteDocClose,
  } = useDisclosure();
  const cancelDeleteDocRef = useRef(null);
  const [documentoPendienteEliminar, setDocumentoPendienteEliminar] = useState(null);
  const [deletingDocumento, setDeletingDocumento] = useState(false);

  const loadDocs = useCallback(async () => {
    try {
      const res = await bandasAPI.listDocumentosGestion();
      setDocumentos(res.data?.documentos || []);
    } catch {
      setDocumentos([]);
    }
  }, []);

  const loadShare = useCallback(async () => {
    setShareLoading(true);
    try {
      const res = await bandasAPI.getDocumentosGestionShare();
      setShare({
        shareUrl: res.data?.shareUrl || '',
        hasPassword: !!res.data?.hasPassword,
        canRevealSharePassword: !!res.data?.canRevealSharePassword,
      });
    } catch {
      setShare({ shareUrl: '', hasPassword: false, canRevealSharePassword: false });
    } finally {
      setShareLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      await loadDocs();
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [loadDocs]);

  useEffect(() => {
    loadShare();
  }, [loadShare]);

  const documentosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    let list = documentos.filter((d) => {
      if (filtroCategoria && d.categoria !== filtroCategoria) return false;
      const enTablon = !!d.publicadoEnTablon;
      if (filtroTablon === 'tablon' && !enTablon) return false;
      if (filtroTablon === 'sin_tablon' && enTablon) return false;
      if (!q) return true;
      return [d.titulo, d.descripcion, d.nombreArchivo].some(
        (s) => s != null && String(s).toLowerCase().includes(q),
      );
    });
    const sorted = [...list];
    const tituloCmp = (a, b) =>
      String(a.titulo || '').localeCompare(String(b.titulo || ''), 'es', { sensitivity: 'base' });
    switch (orden) {
      case 'reciente':
        sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'antiguo':
        sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'titulo_az':
        sorted.sort(tituloCmp);
        break;
      case 'titulo_za':
        sorted.sort((a, b) => tituloCmp(b, a));
        break;
      case 'categoria':
        sorted.sort((a, b) => {
          const c = String(a.categoria || '').localeCompare(String(b.categoria || ''));
          return c !== 0 ? c : tituloCmp(a, b);
        });
        break;
      case 'tamano_desc':
        sorted.sort((a, b) => (b.tamanoBytes ?? 0) - (a.tamanoBytes ?? 0));
        break;
      case 'tamano_asc':
        sorted.sort((a, b) => (a.tamanoBytes ?? 0) - (b.tamanoBytes ?? 0));
        break;
      default:
        break;
    }
    return sorted;
  }, [documentos, busqueda, filtroCategoria, filtroTablon, orden]);

  const onUpload = async () => {
    if (!file) {
      toaster.create({ title: 'Selecciona un archivo', type: 'warning' });
      return;
    }
    if (!titulo.trim()) {
      toaster.create({ title: 'Indica un título', type: 'warning' });
      return;
    }
    setUploading(true);
    try {
      await bandasAPI.uploadDocumentoGestion(file, {
        titulo: titulo.trim(),
        categoria,
        descripcion: descripcion.trim() || undefined,
        publicadoEnTablon: publicadoTablonAlSubir,
      });
      toaster.create({ title: 'Documento subido', type: 'success' });
      closeUploadModal();
      await loadDocs();
    } catch (e) {
      toaster.create({
        title: 'Error al subir',
        description: e?.response?.data?.message || e?.message,
        type: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const abrirDocumento = async (d) => {
    setOpeningId(d.id);
    try {
      const blob = await bandasAPI.fetchDocumentoGestionArchivoBlob(d.id);
      const mime = d.mimeType || blob.type || 'application/octet-stream';
      const blobConTipo = blob.type ? blob : new Blob([blob], { type: mime });
      const url = URL.createObjectURL(blobConTipo);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        URL.revokeObjectURL(url);
        toaster.create({
          title: 'Ventana bloqueada',
          description: 'Permite ventanas emergentes para ver el documento, o descarga el archivo desde el visor del navegador.',
          type: 'warning',
        });
        return;
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 180000);
    } catch (e) {
      toaster.create({
        title: 'No se pudo abrir el documento',
        description: e?.response?.data?.message || e?.message,
        type: 'error',
      });
    } finally {
      setOpeningId(null);
    }
  };

  const patchTablon = async (documentoId, publicadoEnTablon) => {
    setPatchingTablonId(documentoId);
    try {
      await bandasAPI.patchDocumentoGestion(documentoId, { publicadoEnTablon });
      setDocumentos((prev) =>
        prev.map((x) => (x.id === documentoId ? { ...x, publicadoEnTablon } : x)),
      );
    } catch (e) {
      toaster.create({
        title: 'No se pudo actualizar',
        description: e?.response?.data?.message || e?.message,
        type: 'error',
      });
    } finally {
      setPatchingTablonId(null);
    }
  };

  const requestDeleteDocumento = (d) => {
    setDocumentoPendienteEliminar(d);
    onDeleteDocOpen();
  };

  const confirmDeleteDocumento = async () => {
    if (!documentoPendienteEliminar?.id) return;
    try {
      setDeletingDocumento(true);
      await bandasAPI.deleteDocumentoGestion(documentoPendienteEliminar.id);
      toaster.create({ title: 'Eliminado', type: 'success' });
      await loadDocs();
    } catch (e) {
      toaster.create({
        title: 'Error',
        description: e?.response?.data?.message || e?.message,
        type: 'error',
      });
    } finally {
      setDeletingDocumento(false);
      setDocumentoPendienteEliminar(null);
      onDeleteDocClose();
    }
  };

  const copyUrl = async () => {
    if (!share.shareUrl) return;
    try {
      await navigator.clipboard.writeText(share.shareUrl);
      toaster.create({ title: 'Enlace copiado', type: 'success' });
    } catch {
      toaster.create({ title: 'No se pudo copiar', type: 'warning' });
    }
  };

  const shareWhatsapp = () => {
    const url = (share.shareUrl || '').trim();
    if (!url) return;
    const nombre = profile?.nombre || 'nuestra banda';
    const text = encodeURIComponent(
      `Tablón de documentos de *${nombre}* en Cadencya.\n\nAccede con la contraseña que te facilitemos:\n${url}`,
    );
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener');
  };

  const submitSharePwd = async () => {
    if (sharePwd.length < 8) {
      toaster.create({ title: 'Mínimo 8 caracteres', type: 'warning' });
      return;
    }
    if (sharePwd !== sharePwd2) {
      toaster.create({ title: 'Las contraseñas no coinciden', type: 'warning' });
      return;
    }
    setSharePwdSaving(true);
    try {
      await bandasAPI.setDocumentosGestionSharePassword({
        password: sharePwd,
        passwordConfirm: sharePwd2,
      });
      toaster.create({ title: 'Tablón activado', type: 'success' });
      onModalPwdClose();
      setSharePwd('');
      setSharePwd2('');
      await loadShare();
    } catch (e) {
      toaster.create({
        title: 'No se pudo guardar',
        description: e?.response?.data?.message || e?.message,
        type: 'error',
      });
    } finally {
      setSharePwdSaving(false);
    }
  };

  const submitReveal = async () => {
    setRevealLoading(true);
    setRevealed('');
    try {
      const res = await bandasAPI.revealDocumentosGestionSharePassword({
        accountPassword: verCuentaPwd,
      });
      setRevealed(res.data?.sharePassword || '');
      setVerCuentaPwd('');
    } catch (e) {
      toaster.create({
        title: 'No se pudo mostrar',
        description: e?.response?.data?.message || e?.message,
        type: 'error',
      });
    } finally {
      setRevealLoading(false);
    }
  };

  const submitCambiar = async () => {
    if (cambiarNueva.length < 8 || cambiarNueva !== cambiarNueva2) {
      toaster.create({ title: 'Revisa la nueva contraseña', type: 'warning' });
      return;
    }
    setCambiarSaving(true);
    try {
      await bandasAPI.updateDocumentosGestionSharePassword({
        currentPassword: cambiarActual,
        newPassword: cambiarNueva,
        newPasswordConfirm: cambiarNueva2,
      });
      toaster.create({ title: 'Contraseña actualizada', type: 'success' });
      onCambiarClose();
      setCambiarActual('');
      setCambiarNueva('');
      setCambiarNueva2('');
      await loadShare();
    } catch (e) {
      toaster.create({
        title: 'Error',
        description: e?.response?.data?.message || e?.message,
        type: 'error',
      });
    } finally {
      setCambiarSaving(false);
    }
  };

  return (
    <AppLayout userType="banda" userName={profile?.nombre || user?.email || 'Mi banda'}>
      <Box bg={pageBg} minH="100vh" py={{ base: 4, md: 8 }}>
        <Container maxW="6xl">
          <MotionBox
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            borderRadius="2xl"
            p={{ base: 5, md: 8 }}
            bg={heroBg}
            borderWidth="1px"
            borderColor={heroBorder}
            mb={8}
            boxShadow="0 12px 40px -18px rgba(103, 65, 188, 0.35)"
          >
            <HStack gap={4} align="start" flexWrap="wrap">
              <Box
                p={3}
                borderRadius="xl"
                bg="whiteAlpha.900"
                color="purple.600"
                boxShadow="md"
                _dark={{ bg: 'whiteAlpha.100', color: 'purple.200' }}
              >
                <Icon boxSize={10} asChild><FaFileAlt /></Icon>
              </Box>
              <Box flex={1} data-tour="documentos-header">
                <Heading size="xl" letterSpacing="-0.02em">
                  Documentos
                </Heading>
                <Text mt={2} color={subtle} maxW="3xl" fontSize="md">
                  Biblioteca interna de la banda: aquí guardas todo. Decide con el interruptor «En el tablón» qué
                  documentos se muestran en el enlace público: para quien reciba el enlace eso es su{' '}
                  <Box as="span" fontWeight="semibold" color="purple.600">
                    Tablón
                  </Box>
                  , protegido con la misma contraseña del enlace.
                </Text>
              </Box>
            </HStack>
          </MotionBox>

          <VStack gap={6} align="stretch">
            <Card.Root bg={cardBg} shadow="lg" rounded="2xl" overflow="hidden">
              <Box h="3px" bgGradient="linear(to-r, purple.500, cyan.400)" />
              <Card.Header pb={3}>
                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  gap={4}
                  align={{ md: 'flex-start' }}
                  justify="space-between"
                >
                  <Box flex={1} minW={0}>
                    <Heading size="md">Biblioteca de documentos</Heading>
                    <Text fontSize="sm" color={subtle} mt={1}>
                      {loading
                        ? 'Cargando…'
                        : documentos.length === 0
                          ? 'Aún no hay archivos en la biblioteca.'
                          : `${documentos.length} documento${documentos.length === 1 ? '' : 's'}${
                              documentosFiltrados.length !== documentos.length
                                ? ` · ${documentosFiltrados.length} con los filtros actuales`
                                : ''
                            }`}
                    </Text>
                  </Box>
                  <Button
                    colorPalette="purple"
                    onClick={onUploadOpen}
                    disabled={!isTitularBanda}
                    flexShrink={0}
                    alignSelf={{ base: 'stretch', md: 'flex-start' }}
                    data-tour="documentos-upload"><FaUpload />Subir documento
                                      </Button>
                </Flex>
                {!loading && documentos.length > 0 && (
                  <VStack gap={3} align="stretch" mt={5}>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <Icon color="gray.400" asChild><FaSearch /></Icon>
                      </InputLeftElement>
                      <Input
                        pl={10}
                        placeholder="Buscar por título, descripción o nombre de archivo…"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        borderRadius="xl"
                      />
                    </InputGroup>
                    <Flex
                      direction={{ base: 'column', lg: 'row' }}
                      gap={3}
                      align={{ lg: 'flex-end' }}
                      flexWrap="wrap"
                    >
                      <Field.Root flex={{ lg: '1 1 160px' }} minW={0}>
                        <Field.Label fontSize="xs" mb={1} color={subtle}>
                          Categoría
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            size="sm"
                            borderRadius="lg"
                            value={filtroCategoria}
                            onChange={(e) => setFiltroCategoria(e.target.value)}>
                            <option value="">Todas las categorías</option>
                            {CATEGORIAS.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                      <Field.Root flex={{ lg: '1 1 150px' }} minW={0}>
                        <Field.Label fontSize="xs" mb={1} color={subtle}>
                          Tablón
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            size="sm"
                            borderRadius="lg"
                            value={filtroTablon}
                            onChange={(e) => setFiltroTablon(e.target.value)}>
                            <option value="">Todos</option>
                            <option value="tablon">En el tablón</option>
                            <option value="sin_tablon">No en el tablón</option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                      <Field.Root flex={{ lg: '1 1 200px' }} minW={0}>
                        <Field.Label fontSize="xs" mb={1} color={subtle}>
                          Ordenar por
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            size="sm"
                            borderRadius="lg"
                            value={orden}
                            onChange={(e) => setOrden(e.target.value)}>
                            {ORDEN_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                      <Field.Root flexShrink={0} w={{ base: 'full', lg: 'auto' }}>
                        <Field.Label fontSize="xs" mb={1} color={subtle}>
                          Visualización
                        </Field.Label>
                        <ButtonGroup attached size="sm" w={{ base: 'full', lg: 'auto' }}>
                          <Button
                            variant={vista === 'lista' ? 'solid' : 'outline'}
                            colorPalette="purple"
                            flex={{ base: 1, lg: 'initial' }}
                            onClick={() => setVistaPersist('lista')}
                            aria-pressed={vista === 'lista'}><FaList />Lista
                                                      </Button>
                          <Button
                            variant={vista === 'mosaico' ? 'solid' : 'outline'}
                            colorPalette="purple"
                            flex={{ base: 1, lg: 'initial' }}
                            onClick={() => setVistaPersist('mosaico')}
                            aria-pressed={vista === 'mosaico'}><FaTh />Mosaico
                                                      </Button>
                        </ButtonGroup>
                      </Field.Root>
                    </Flex>
                  </VStack>
                )}
              </Card.Header>
              <Card.Body pt={0} data-tour="documentos-list">
                {loading ? (
                  <Spinner />
                ) : documentos.length === 0 ? (
                  <VStack py={8} gap={4} align="center">
                    <Text color={subtle} textAlign="center" maxW="md">
                      Sube estatutos, actas o normativa con el botón «Subir documento». Luego podrás filtrarlos por
                      categoría, tablón, búsqueda, ordenarlos y elegir vista en lista o mosaico.
                    </Text>
                    {isTitularBanda && (
                      <Button colorPalette="purple" variant="outline" onClick={onUploadOpen}><FaUpload />Subir el primero
                                              </Button>
                    )}
                    {!isTitularBanda && (
                      <Text fontSize="xs" color={subtle}>
                        Solo el titular de la cuenta puede subir documentos.
                      </Text>
                    )}
                  </VStack>
                ) : documentosFiltrados.length === 0 ? (
                  <Text color={subtle} py={6}>
                    Ningún documento coincide con la búsqueda o el filtro. Prueba a limpiar criterios.
                  </Text>
                ) : vista === 'lista' ? (
                  <AnimatePresence>
                    <VStack align="stretch" gap={3}>
                      {documentosFiltrados.map((d, i) => (
                        <MotionBox
                          key={d.id}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2, delay: Math.min(i * 0.02, 0.24) }}
                        >
                          <HStack
                            p={4}
                            borderWidth="1px"
                            borderRadius="xl"
                            borderColor={docRowBorder}
                            justify="space-between"
                            align="start"
                            _hover={{ borderColor: docRowHoverBorder, bg: docRowHoverBg }}
                            transition="border-color 0.2s, background 0.2s"
                          >
                            <Box minW={0} flex={1}>
                              <Text fontWeight="700" lineClamp={2}>
                                {d.titulo}
                              </Text>
                              <HStack mt={1} gap={2} flexWrap="wrap">
                                <Badge colorPalette="purple">{labelCategoria(d.categoria)}</Badge>
                                {d.nombreArchivo && (
                                  <Text fontSize="xs" color={subtle} lineClamp={1} maxW="200px" title={d.nombreArchivo}>
                                    {d.nombreArchivo}
                                  </Text>
                                )}
                                {d.tamanoBytes != null && (
                                  <Text fontSize="xs" color={subtle}>
                                    {(d.tamanoBytes / 1024).toFixed(0)} KB
                                  </Text>
                                )}
                                {d.createdAt && (
                                  <Text fontSize="xs" color={subtle}>
                                    {new Date(d.createdAt).toLocaleDateString('es-ES', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                    })}
                                  </Text>
                                )}
                              </HStack>
                              {d.descripcion && (
                                <Text fontSize="sm" color={subtle} mt={2} lineClamp={3}>
                                  {d.descripcion}
                                </Text>
                              )}
                              <HStack mt={3} gap={3} align="center" flexWrap="wrap">
                                {isTitularBanda ? (
                                  <Field.Root display="flex" alignItems="center" width="auto" minW="min-content">
                                    <Field.Label fontSize="xs" mb={0} mr={2} htmlFor={`tablon-${d.id}`}>
                                      En el tablón
                                    </Field.Label>
                                    <Switch
                                      id={`tablon-${d.id}`}
                                      colorPalette="purple"
                                      size="sm"
                                      checked={!!d.publicadoEnTablon}
                                      disabled={patchingTablonId === d.id}
                                      onChange={(e) => patchTablon(d.id, e.target.checked)}
                                    />
                                  </Field.Root>
                                ) : (
                                  <Badge
                                    colorPalette={d.publicadoEnTablon ? 'green' : 'gray'}
                                    variant={d.publicadoEnTablon ? 'solid' : 'subtle'}
                                  >
                                    {d.publicadoEnTablon ? 'En el tablón público' : 'Solo biblioteca'}
                                  </Badge>
                                )}
                              </HStack>
                            </Box>
                            <HStack gap={1} flexShrink={0}>
                              <Button
                                size="sm"
                                variant="ghost"
                                colorPalette="purple"
                                loading={openingId === d.id}
                                loadingText="Abriendo"
                                onClick={() => abrirDocumento(d)}><FaExternalLinkAlt />Abrir
                                                              </Button>
                              {isTitularBanda && (
                                <IconButton
                                  aria-label="Eliminar"
                                  size="sm"
                                  variant="ghost"
                                  colorPalette="red"
                                  onClick={() => requestDeleteDocumento(d)}><FaTrash /></IconButton>
                              )}
                            </HStack>
                          </HStack>
                        </MotionBox>
                      ))}
                    </VStack>
                  </AnimatePresence>
                ) : (
                  <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                    {documentosFiltrados.map((d, i) => (
                      <MotionBox
                        key={d.id}
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2, delay: Math.min(i * 0.03, 0.3) }}
                      >
                        <Box
                          p={4}
                          borderWidth="1px"
                          borderRadius="xl"
                          borderColor={docRowBorder}
                          bg={cardBg}
                          h="full"
                          minH="220px"
                          display="flex"
                          flexDirection="column"
                          _hover={{ borderColor: docRowHoverBorder, bg: docRowHoverBg }}
                          transition="border-color 0.2s, background 0.2s"
                          boxShadow="sm"
                        >
                          <HStack justify="space-between" align="start" mb={2}>
                            <Flex
                              align="center"
                              justify="center"
                              w={10}
                              h={10}
                              borderRadius="lg"
                              bg="purple.50"
                              color="purple.600"
                              _dark={{ bg: 'whiteAlpha.100', color: 'purple.200' }}
                            >
                              <Icon boxSize={5} asChild><FaFileAlt /></Icon>
                            </Flex>
                            {isTitularBanda && (
                              <IconButton
                                aria-label="Eliminar"
                                size="xs"
                                variant="ghost"
                                colorPalette="red"
                                onClick={() => requestDeleteDocumento(d)}><FaTrash /></IconButton>
                            )}
                          </HStack>
                          <Text fontWeight="700" fontSize="md" lineClamp={2} lineHeight="short">
                            {d.titulo}
                          </Text>
                          <Badge colorPalette="purple" alignSelf="start" mt={2}>
                            {labelCategoria(d.categoria)}
                          </Badge>
                          <VStack align="stretch" gap={0} mt={2} flex={1}>
                            {d.nombreArchivo && (
                              <Text fontSize="xs" color={subtle} lineClamp={1} title={d.nombreArchivo}>
                                {d.nombreArchivo}
                              </Text>
                            )}
                            <HStack gap={2} flexWrap="wrap" fontSize="xs" color={subtle}>
                              {d.tamanoBytes != null && <Text>{(d.tamanoBytes / 1024).toFixed(0)} KB</Text>}
                              {d.createdAt && (
                                <Text>
                                  {new Date(d.createdAt).toLocaleDateString('es-ES', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })}
                                </Text>
                              )}
                            </HStack>
                            {d.descripcion && (
                              <Text fontSize="sm" color={subtle} mt={2} lineClamp={3} flex={1}>
                                {d.descripcion}
                              </Text>
                            )}
                          </VStack>
                          <HStack
                            mt={3}
                            pt={3}
                            borderTopWidth="1px"
                            borderTopColor={docRowBorder}
                            justify="space-between"
                            align="center"
                            gap={2}
                          >
                            {isTitularBanda ? (
                              <>
                                <Text fontSize="xs" color={subtle} flex={1} lineClamp={2}>
                                  Mostrar en tablón público
                                </Text>
                                <Switch
                                  colorPalette="purple"
                                  size="sm"
                                  flexShrink={0}
                                  checked={!!d.publicadoEnTablon}
                                  disabled={patchingTablonId === d.id}
                                  onChange={(e) => patchTablon(d.id, e.target.checked)}
                                  aria-label="Publicar en el tablón"
                                />
                              </>
                            ) : (
                              <Badge
                                colorPalette={d.publicadoEnTablon ? 'green' : 'gray'}
                                variant={d.publicadoEnTablon ? 'solid' : 'subtle'}
                                fontSize="xs"
                              >
                                {d.publicadoEnTablon ? 'En el tablón' : 'Solo biblioteca'}
                              </Badge>
                            )}
                          </HStack>
                          <Button
                            mt={3}
                            size="sm"
                            colorPalette="purple"
                            loading={openingId === d.id}
                            loadingText="Abriendo"
                            onClick={() => abrirDocumento(d)}
                            w="full"><FaExternalLinkAlt />Abrir
                                                      </Button>
                        </Box>
                      </MotionBox>
                    ))}
                  </SimpleGrid>
                )}
              </Card.Body>
            </Card.Root>

            <Card.Root bg={cardBg} shadow="lg" rounded="2xl" overflow="hidden" data-tour="documentos-tablon-share">
              <Box h="3px" bgGradient="linear(to-r, cyan.400, purple.500)" />
              <Card.Header>
                <HStack>
                  <Icon color="purple.500" asChild><FaShareAlt /></Icon>
                  <Heading size="md">Enlace al tablón público</Heading>
                </HStack>
                <Text fontSize="sm" color={subtle} mt={2}>
                  Activa una contraseña para generar el enlace. Los invitados verán la vista «Tablón» solo con los
                  archivos que marques como publicados en el tablón (el resto de la biblioteca no se muestra).
                </Text>
              </Card.Header>
              <Card.Body>
                {shareLoading ? (
                  <Spinner />
                ) : share.hasPassword ? (
                  <VStack align="stretch" gap={3}>
                    <Field.Root>
                      <Field.Label fontSize="sm">Enlace</Field.Label>
                      <HStack gap={2} w="full" align="stretch">
                        <Input
                          flex="1"
                          minW={0}
                          size="md"
                          readOnly
                          value={share.shareUrl}
                          fontSize="sm"
                          title={share.shareUrl}
                        />
                        <IconButton
                          aria-label="Copiar"
                          flexShrink={0}
                          onClick={copyUrl}
                        ><FaCopy /></IconButton>
                        <IconButton
                          aria-label="WhatsApp"
                          flexShrink={0}
                          colorPalette="green"
                          onClick={shareWhatsapp}
                        ><FaWhatsapp /></IconButton>
                      </HStack>
                    </Field.Root>
                    {isTitularBanda && (
                      <HStack flexWrap="wrap" gap={2}>
                        <Button size="sm" variant="outline" onClick={onCambiarOpen}>
                          Cambiar contraseña del tablón
                        </Button>
                        {share.canRevealSharePassword && (
                          <Button size="sm" variant="ghost" onClick={onRevealOpen}>
                            Ver contraseña del tablón
                          </Button>
                        )}
                      </HStack>
                    )}
                  </VStack>
                ) : (
                  <VStack align="stretch" gap={3}>
                    <Alert.Root status="info" rounded="lg" fontSize="sm">
                      <Alert.Indicator />
                      Aún no hay enlace al tablón. Define una contraseña de acceso para generarlo.
                    </Alert.Root>
                    {isTitularBanda && (
                      <Button colorPalette="purple" onClick={onModalPwdOpen}><FaLock />Activar tablón con contraseña
                                              </Button>
                    )}
                  </VStack>
                )}
              </Card.Body>
            </Card.Root>
          </VStack>
        </Container>
      </Box>

      <Dialog.Root open={uploadOpen} size='lg' placement='center' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          closeUploadModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.400" backdropFilter="blur(6px)" />
          <Dialog.Positioner>
            <Dialog.Content borderRadius="2xl" maxH="90vh" my={{ base: 4, md: 'auto' }}>
              <Dialog.Header>Subir documento</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Text fontSize="sm" color={subtle} mb={4}>
                  PDF, Word o imagen (máx. 15 MB). Los datos se guardan en tu espacio de banda.
                </Text>
                <VStack gap={4} align="stretch">
                  <Field.Root required>
                    <Field.Label>Título</Field.Label>
                    <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ej. Acta asamblea 2025" />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Categoría</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                        {CATEGORIAS.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Descripción (opcional)</Field.Label>
                    <Textarea
                      value={descripcion}
                      onChange={(e) => setDescripcion(e.target.value)}
                      rows={3}
                      placeholder="Notas para tu junta o para quien reciba el enlace"
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Archivo</Field.Label>
                    <Input type="file" accept=".pdf,.doc,.docx,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  </Field.Root>
                  {isTitularBanda && (
                    <Field.Root display="flex" alignItems="flex-start" flexDirection="column" gap={1}>
                      <HStack justify="space-between" w="full" align="center">
                        <Field.Label mb={0}>Publicar en el tablón al subir</Field.Label>
                        <Switch
                          colorPalette="purple"
                          checked={publicadoTablonAlSubir}
                          onChange={(e) => setPublicadoTablonAlSubir(e.target.checked)}
                        />
                      </HStack>
                      <Field.HelperText>
                        Si lo activas, quien use el enlace público verá este archivo en el Tablón. Siempre puedes cambiarlo
                        después en el listado.
                      </Field.HelperText>
                    </Field.Root>
                  )}
                  {!isTitularBanda && (
                    <Text fontSize="xs" color={subtle}>
                      Solo el titular de la cuenta puede subir documentos.
                    </Text>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={closeUploadModal}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={onUpload}
                  loading={uploading}
                  disabled={!isTitularBanda}><FaUpload />Subir
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root open={modalPwdOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onModalPwdClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Contraseña del tablón público</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={3} align="stretch">
                  <Text fontSize="sm" color={subtle}>
                    Protege el enlace al Tablón. Solo verán los documentos que marques como «En el tablón».
                  </Text>
                  <Field.Root>
                    <Field.Label>Contraseña</Field.Label>
                    <Input type="password" value={sharePwd} onChange={(e) => setSharePwd(e.target.value)} autoComplete="new-password" />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Repetir contraseña</Field.Label>
                    <Input type="password" value={sharePwd2} onChange={(e) => setSharePwd2(e.target.value)} />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onModalPwdClose}>
                  Cancelar
                </Button>
                <Button colorPalette="purple" onClick={submitSharePwd} loading={sharePwdSaving}>
                  Guardar y generar enlace al tablón
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root open={revealOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onRevealClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Ver contraseña del tablón</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={3} align="stretch">
                  <Text fontSize="sm">Introduce tu contraseña de cuenta Cadencya.</Text>
                  <Field.Root>
                    <Field.Label>Contraseña de cuenta</Field.Label>
                    <Input type="password" value={verCuentaPwd} onChange={(e) => setVerCuentaPwd(e.target.value)} />
                  </Field.Root>
                  {revealed && (
                    <HStack>
                      <Input readOnly type={showRevealed ? 'text' : 'password'} value={revealed} />
                      <IconButton aria-label="Mostrar" onClick={() => setShowRevealed((s) => !s)}>{showRevealed ? <FaEyeSlash /> : <FaEye />}</IconButton>
                      <IconButton
                        aria-label="Copiar"
                        onClick={() => navigator.clipboard.writeText(revealed)}><FaCopy /></IconButton>
                    </HStack>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onRevealClose}>
                  Cerrar
                </Button>
                <Button colorPalette="purple" onClick={submitReveal} loading={revealLoading} disabled={!!revealed}>
                  Mostrar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root open={cambiarOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onCambiarClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Cambiar contraseña del tablón</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={3}>
                  <Field.Root>
                    <Field.Label>Contraseña actual del tablón</Field.Label>
                    <Input type="password" value={cambiarActual} onChange={(e) => setCambiarActual(e.target.value)} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Nueva contraseña</Field.Label>
                    <Input type="password" value={cambiarNueva} onChange={(e) => setCambiarNueva(e.target.value)} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Repetir nueva</Field.Label>
                    <Input type="password" value={cambiarNueva2} onChange={(e) => setCambiarNueva2(e.target.value)} />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onCambiarClose}>
                  Cancelar
                </Button>
                <Button colorPalette="purple" onClick={submitCambiar} loading={cambiarSaving}>
                  Guardar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={isDeleteDocOpen}
        initialFocusEl={() => cancelDeleteDocRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            if (deletingDocumento) return;
            setDocumentoPendienteEliminar(null);
            onDeleteDocClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Eliminar documento
                </Dialog.Header>
                <Dialog.Body>
                  <Text fontSize="sm" color="gray.700">
                    ¿Seguro que quieres eliminar{' '}
                    <strong>{documentoPendienteEliminar?.titulo || 'este documento'}</strong>? Esta acción no se puede deshacer.
                  </Text>
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button
                    ref={cancelDeleteDocRef}
                    onClick={() => {
                      setDocumentoPendienteEliminar(null);
                      onDeleteDocClose();
                    }}
                    disabled={deletingDocumento}
                  >
                    Cancelar
                  </Button>
                  <Button colorPalette="red" onClick={confirmDeleteDocumento} loading={deletingDocumento}>
                    Eliminar
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </AppLayout>
  );
}
